import { Box, Button, Tooltip, Typography } from '@mui/material'
import {
    ChangeEvent,
    memo,
    ReactElement,
    useCallback,
    useMemo,
    useState,
} from 'react'
import RealTimeAlertEventsCheckbox from './RealTimeAlertEventsCheckbox'
import ErrorBoundary from '../../ErrorBoundaries/ErrorBoundary'
import { ISelectedAttributes } from '../interfaces'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
    DetailData,
    ITimeSeriesDataTableAPIResponse,
    TimeseriesTableColumn,
} from '../../../services/apiInterfaces'
import {
    fetchTimeseriesTableData,
    exportEntityRecordsAsTimeSeriesXLS,
} from '../../../services/entityServices'
import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table'
import MRTTable from '../../Common/Table'
import { AttributeType } from '../../../constants/constants'
import { formatDateTimeWithUserPreferences } from '../../../utils/DateAndTimeUtils'
import TruncatedText from '../../Common/TruncatedText'
import JSONTypeData from '../../Common/JSONTypeData'
import URLTypeData from '../../Common/URLTypeData'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { IKeyValuePair, IWayPointAndPeriod } from '../../Entity/interfaces'
import GeoJSONTypeData from '../../Common/GeoJsonTypeData'
import { getAllTimezoneOptions } from '../../../utils/TimezoneOptions'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

interface ITimeSeriesDataTable {
    selectedAttributes: ISelectedAttributes[]
    viewID: string
    startDate: string
    endDate: string
    orgId: string
    filterOutlierLocations: boolean
    wayPointsSegments: IWayPointAndPeriod[]
    selectedPeriod: string
    includeStage?: boolean
    clampToRelationStart?: boolean
}

interface IAttributeRangeObject {
    [key: string]: Record<string, string>
}
const TimeSeriesDataTable = (props: ITimeSeriesDataTable): ReactElement => {
    const {
        endDate,
        orgId,
        selectedAttributes,
        startDate,
        viewID,
        filterOutlierLocations,
        wayPointsSegments,
        selectedPeriod,
        includeStage,
        clampToRelationStart,
    } = props
    const org = useAppSelector((state) => state.org.org)
    const [filterTriggeredReadings, setFilterTriggeredReadings] = useState(
        org?.preferences?.data?.excludeTriggeredEvents === 'true',
    )
    const { t } = useTranslation()
    const user = useAppSelector((state) => state.user.user)
    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'
    const guestUserPreferredUnit = useAppSelector(
        (state) => state.entityInstanceView.guestUserPreferredUnit,
    )
    const dispatch = useAppDispatch()
    const [sorting, setSorting] = useState<MRT_SortingState>([
        {
            id: 'Time',
            desc: true,
        },
    ])
    const selectedSegment = wayPointsSegments.find(
        (segment) => segment.segmentName === selectedPeriod,
    )
    const segmentName =
        (selectedSegment?.type === 'transitPort'
            ? `Trans-Shipment – ${selectedSegment.segmentName}`
            : selectedSegment?.segmentName) || ''

    const sortBy = sorting[0]?.desc ? 'DESC' : 'ASC'
    const queryKey = useMemo(
        () => [
            'timeseries-table-data',
            selectedAttributes,
            startDate,
            endDate,
            orgId,
            viewID,
            user,
            sortBy,
            guestUserPreferredUnit,
            filterTriggeredReadings,
            clampToRelationStart,
        ],
        [
            selectedAttributes,
            startDate,
            endDate,
            orgId,
            viewID,
            user,
            sortBy,
            guestUserPreferredUnit,
            filterTriggeredReadings,
            clampToRelationStart,
        ],
    )
    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<ITimeSeriesDataTableAPIResponse>({
            queryKey,
            queryFn: async ({ pageParam }) => {
                const response = await fetchTimeseriesTableData({
                    viewID,
                    selectedAttributes,
                    startDate,
                    endDate,
                    orgId,
                    pageNumber: (pageParam as number) + 1,
                    pageSize: 20,
                    filterOutlierLocations,
                    isGuestUser,
                    filterOutNonTimeSeriesData: true,
                    sortBy,
                    guestUserPreferredUnit,
                    includeStage,
                    filterLightTriggered: filterTriggeredReadings,
                    filterShockTriggered: filterTriggeredReadings,
                    clampToRelationStart,
                })
                return response as ITimeSeriesDataTableAPIResponse
            },
            initialPageParam: 0,
            getNextPageParam: (lastPage, pages) => {
                // // Check if we actually received any data
                // if (!lastPage?.details?.data?.length) {
                //     return undefined // Return undefined to signal no more pages
                // }

                // // Calculate if we've received all the data
                // const totalFetched = pages.reduce(
                //     (acc, page) => acc + (page?.details?.data?.length || 0),
                //     0,
                // )
                // if (totalFetched >= (lastPage?.details?.totalRowCount || 0)) {
                //     return undefined // Return undefined if we've fetched everything
                // }

                return pages?.length // Return next page number
            },
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            enabled: !!selectedAttributes && !!startDate && !!endDate,
        })

    const flatData = useMemo(
        () => data?.pages?.flatMap((page) => page?.details?.data) ?? [],
        [data],
    )

    const totalDBRowCount = data?.pages?.[0]?.details?.totalRowCount ?? 0

    // get user preferred timezone
    const preferredTimeZone: string =
        user?.preferences?.localization?.['timeZone'] ||
        Intl.DateTimeFormat().resolvedOptions().timeZone

    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const userTimeZone = userLocalization?.timeZone || dayjs.tz.guess()

    const selectedTimezoneLabel =
        getAllTimezoneOptions().find((opt) => opt.value === preferredTimeZone)
            ?.label || preferredTimeZone

    const attributeRangeObject = useMemo(() => {
        const attributeRangeObject: IAttributeRangeObject = selectedAttributes
            .filter(
                (item) =>
                    item.attribute?.dataType?.toLocaleLowerCase() ===
                    AttributeType.SELECT,
            )
            .reduce((acc, item) => {
                const serializedRange = item.attribute.range?.trim() || '[]'
                const deSerializedRange: IKeyValuePair[] =
                    JSON.parse(serializedRange)
                const rangeObj = deSerializedRange.reduce(
                    (accumulator, { key, value }) => {
                        accumulator[key] = value
                        return accumulator
                    },
                    {} as Record<string, string>,
                )
                acc[`${item.entityInstanceId}-${item.attribute.id}`] = rangeObj
                return acc
            }, {} as IAttributeRangeObject)
        return attributeRangeObject
    }, [selectedAttributes])

    const tableColumns: MRT_ColumnDef<DetailData>[] = useMemo(
        () =>
            (data?.pages?.[0]?.details?.totalRowCount ?? 0) > 0
                ? data?.pages?.[0]?.details?.columns?.map(
                      (item: TimeseriesTableColumn, index: number) => {
                          if (item.type.toLowerCase() === AttributeType.JSON) {
                              return {
                                  accessorKey: item.name,
                                  header: t(item.name) + item.suffix,
                                  dataType: item.type,
                                  Cell: (row) => (
                                      <JSONTypeData
                                          dataKey={item.name}
                                          dataValue={
                                              row.row.original[item.name]
                                          }
                                      />
                                  ),
                                  Header: (d) => (
                                      <Box sx={{ width: '100%' }}>
                                          <TruncatedText
                                              sx={{
                                                  fontWeight: '600',
                                                  fontSize: '14px',
                                                  paddingTop: '5px',
                                              }}
                                              text={t(
                                                  d.column.columnDef.header,
                                              )}
                                          />
                                      </Box>
                                  ),
                                  enableSorting: false,
                              }
                          } else if (
                              item.type.toLowerCase() === AttributeType.URL
                          ) {
                              return {
                                  accessorKey: item.name,
                                  header: t(item.name) + item.suffix,
                                  dataType: item.type,
                                  Cell: (row) => (
                                      <URLTypeData
                                          dataValue={
                                              row.row.original[
                                                  item.name
                                              ] as string
                                          }
                                      />
                                  ),
                                  Header: (d) => (
                                      <Box sx={{ width: '100%' }}>
                                          <TruncatedText
                                              sx={{
                                                  fontWeight: '600',
                                                  fontSize: '14px',
                                                  paddingTop: '5px',
                                              }}
                                              text={t(
                                                  d.column.columnDef.header,
                                              )}
                                          />
                                      </Box>
                                  ),
                                  enableSorting: false,
                              }
                          } else if (
                              item.name.toLowerCase() === AttributeType.TIME
                          ) {
                              return {
                                  accessorKey: item.name,
                                  header: t(item.name) + item.suffix,
                                  Header: ({ column }) => (
                                      <Box
                                          sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-start',
                                          }}
                                      >
                                          <Typography
                                              variant="body2"
                                              component="span"
                                              sx={{
                                                  fontWeight: 'bold',
                                                  fontSize: '14px',
                                              }}
                                          >
                                              {t(column.columnDef.header)}
                                              {preferredTimeZone ? (
                                                  <span
                                                      style={{
                                                          color: 'gray',
                                                      }}
                                                  >
                                                      {' ' +
                                                          selectedTimezoneLabel}
                                                  </span>
                                              ) : (
                                                  ''
                                              )}
                                          </Typography>
                                      </Box>
                                  ),
                                  dataType: item.type,
                                  Cell: (row) => (
                                      <span>
                                          {formatDateTimeWithUserPreferences(
                                              row.row.original[item.name],
                                              userLocalization,
                                          )}
                                      </span>
                                  ),
                                  enableSorting: true,
                              }
                          } else if (
                              item.type.toLowerCase() === AttributeType.GPS
                          ) {
                              const value = {
                                  accessorKey: item.name,
                                  header: t(item.name) + item.suffix,
                                  dataType: item.type,
                              }

                              return {
                                  accessorKey: item.name,
                                  header: t(item.name) + item.suffix,
                                  dataType: item.type,
                                  Cell: (row) => {
                                      const rowData =
                                          row.row.original[item.name] || ''

                                      let data = { lat: 0, long: 0 }

                                      if (typeof rowData === 'string') {
                                          const parts = rowData.split(',')
                                          data = {
                                              lat: Number.parseFloat(
                                                  parts[0]
                                                      ?.split(':')[1]
                                                      ?.trim() || '',
                                              ),
                                              long: Number.parseFloat(
                                                  parts[1]
                                                      ?.split(':')[1]
                                                      ?.trim() || '',
                                              ),
                                          }
                                      }
                                      return (
                                          <GeoJSONTypeData
                                              fontSize="14px"
                                              index={index}
                                              dataValue={
                                                  Number.isNaN(data.lat) ||
                                                  Number.isNaN(data.long)
                                                      ? ''
                                                      : data
                                              }
                                              item={value}
                                          />
                                      )
                                  },
                                  enableSorting: false,
                              }
                          } else if (
                              item.type.toLowerCase() === AttributeType.SELECT
                          ) {
                              return {
                                  accessorKey: item.name,
                                  header: t(item.name) + item.suffix,
                                  dataType: item.type,
                                  Cell: (row) => (
                                      <span>
                                          {attributeRangeObject[
                                              `${item.entityInstanceId}-${item.attributeId}`
                                          ][row.row.original[item.name]] ||
                                              row.row.original[item.name]}
                                      </span>
                                  ),
                                  enableSorting: false,
                              }
                          } else {
                              return {
                                  accessorKey: item.name || '',
                                  header: t(item.name) + item.suffix || '',
                                  dataType: item.type || '',
                                  Cell: (row) => (
                                      <span>
                                          {row.row.original[item.name] ?? ''}
                                      </span>
                                  ),
                                  enableSorting: false,
                              }
                          }
                      },
                  ) ?? []
                : [],
        [
            /**
             * Don't include data?.pages as dependency here.
             * If it is added then for each page loaded upon scroll this columns object will be created
             */
            data?.pages?.[0]?.details?.columns,
            data?.pages?.[0]?.details?.totalRowCount,
            attributeRangeObject,
            preferredTimeZone,
            selectedTimezoneLabel,
            t,
            userLocalization,
        ],
    )

    const handleExportTimeSeries = async () => {
        dispatch(updateLoader(true))
        await exportEntityRecordsAsTimeSeriesXLS({
            viewID,
            selectedAttributes,
            startDate,
            endDate,
            orgId,
            pageNumber: 1,
            pageSize: 10000,
            filterOutlierLocations,
            isGuestUser,
            filterOutNonTimeSeriesData: true,
            userTimeZone,
            guestUserPreferredUnit,
            segment: segmentName,
            includeStage,
            filterLightTriggered: filterTriggeredReadings,
            filterShockTriggered: filterTriggeredReadings,
        })
        dispatch(updateLoader(false))
    }

    const handleFilterTriggeredReadingsChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setFilterTriggeredReadings(!e.target.checked)
        },
        [],
    )

    return (
        <Box sx={{ marginTop: '20px' }}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent={'space-between'}
                sx={{
                    background: '#DCECF9',
                    padding: '15px',
                    borderRadius: '5px 5px 0px 0px',
                }}
            >
                <Box display="flex" alignItems="center">
                    <Typography variant="h6">{t('tableData')}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <RealTimeAlertEventsCheckbox
                        checked={!filterTriggeredReadings}
                        onChange={handleFilterTriggeredReadingsChange}
                        sx={{ mr: 0 }}
                    />
                    <Button
                        onClick={handleExportTimeSeries}
                        id="timeseries-table-button-export"
                        sx={{
                            minWidth: '56px',
                            maxWidth: '56px',
                        }}
                    >
                        <Tooltip title={t('exportAsXls')} placement="top">
                            <img
                                src="/assets/images/icons/file_save.svg"
                                alt="Export"
                                style={{ marginRight: '8px' }}
                            />
                        </Tooltip>
                    </Button>
                </Box>
            </Box>
            <Box
                sx={{
                    background: '#F5FBFF',
                    marginBottom: '10px',
                }}
                width="100%"
            >
                {tableColumns?.length === 0 &&
                !isError &&
                !isFetching &&
                !isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px',
                        }}
                    >
                        <Typography variant="body1">
                            {t('noDataForSelectedAttributesAndTime')}
                        </Typography>
                    </Box>
                ) : (
                    <MRTTable<DetailData, ITimeSeriesDataTableAPIResponse>
                        columns={tableColumns}
                        sorting={sorting} // Adjust sorting state if needed
                        globalFilter={''} // Adjust if search functionality is needed
                        columnFilters={[]} // Adjust filters if needed
                        setColumnFilters={() => {}} // Provide state update function if needed
                        setGlobalFilter={() => {}} // Provide state update function if needed
                        setSorting={setSorting} // Provide state update function if needed
                        data={flatData ?? []}
                        totalDBRowCount={totalDBRowCount}
                        fetchNextPage={fetchNextPage}
                        isError={isError}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        enableBottomToolbar={true}
                        isCurvedBottomToolbar={false}
                        bottomToolbarPadding={'0px'}
                        enableRowSelection={false}
                        enableSorting={undefined}
                        maxHeight="400px"
                    />
                )}
            </Box>
        </Box>
    )
}

const TimeSeriesDataTableWrapper = memo((props: ITimeSeriesDataTable) => (
    <ErrorBoundary>
        <TimeSeriesDataTable {...props} />
    </ErrorBoundary>
))

TimeSeriesDataTableWrapper.displayName = 'TimeSeriesDataTable'

export default TimeSeriesDataTableWrapper
